import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
  fetchConversations, fetchMessages, sendReply, addNote,
  updateConversationStatus, markConversationAsLead, setConversationAiState, setActiveConversation, setFilters,
  appendMessage,
} from '../../redux/inboxSlice';
import ConversationThread from './ConversationThread';
import ReplyBox from './ReplyBox';
import api from '../../services/api';

const CHANNEL_ICONS = {
  whatsapp: { label: 'WhatsApp', color: '#25D366', emoji: '📱' },
  instagram: { label: 'Instagram', color: '#E1306C', emoji: '📸' },
  messenger: { label: 'Messenger', color: '#0084FF', emoji: '💬' },
  support_widget: { label: 'Support Widget', color: '#0176D3', emoji: '🤖' },
  telegram: { label: 'Telegram', color: '#229ED9', emoji: '✈️' },
  email: { label: 'Email', color: '#5C5C5C', emoji: '✉️' },
  voice: { label: 'Voice', color: '#8E44AD', emoji: '📞' },
  sms: { label: 'SMS', color: '#2E844A', emoji: '💬' },
};

// Channels the support agent can answer on (staff can take over / hand back on any of them).
const AI_CHANNELS = ['support_widget', 'whatsapp', 'instagram', 'messenger', 'email', 'telegram'];

const AI_STATE_BADGE = {
  ai: { label: 'AI answering', className: 'bg-[#EEF4FF] text-[#0176D3]' },
  escalated: { label: 'Needs a human', className: 'bg-[#FDE7E9] text-[#8E030F]' },
  human: { label: "You're live", className: 'bg-[#E3F3E8] text-[#036B3A]' },
};

const STATUS_COLORS = {
  open: 'bg-[#E3F3E8] text-[#036B3A]',
  pending: 'bg-[#FFF4E5] text-[#8C4B02]',
  resolved: 'bg-[#F3F2F2] text-[#706E6B]',
  bot: 'bg-[#EEF4FF] text-[#0176D3]',
};

export default function InboxPage() {
  const dispatch = useDispatch();
  const { conversations, activeConversationId, messages, messagesPagination, loading, messagesLoading, messagesLoadingMore, filters } = useSelector(s => s.inbox);
  const token = localStorage.getItem('token');
  const socketRef = useRef(null);
  const [isNoteMode, setIsNoteMode] = useState(false);
  const [sending, setSending] = useState(false);
  const [draftGone, setDraftGone] = useState(null); // conversation whose AI draft was used or dismissed
  const [searchParams] = useSearchParams();

  // Deep-link from a notification (bell / Instagram tab badge) — jump straight
  // to the conversation it's about instead of just landing on the inbox list.
  useEffect(() => {
    const conversationId = searchParams.get('conversationId');
    if (conversationId) dispatch(setActiveConversation(conversationId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Socket connection
  useEffect(() => {
    const socket = io('/inbox', { auth: { token }, transports: ['websocket'] });
    socketRef.current = socket;

    socket.on('inbox:new_message', ({ conversationId, message }) => {
      dispatch(appendMessage({ ...message, conversationId }));
    });
    socket.on('inbox:conversation_updated', () => {
      dispatch(fetchConversations(filters));
    });

    return () => socket.disconnect();
  }, [token]);

  useEffect(() => {
    dispatch(fetchConversations(filters));
  }, [filters]);

  useEffect(() => {
    if (activeConversationId) {
      dispatch(fetchMessages({ conversationId: activeConversationId }));
    }
  }, [activeConversationId]);

  const activeConv = conversations.find(c => c._id === activeConversationId);

  const handleSend = async (text, media) => {
    if (!activeConversationId || (!text.trim() && !media)) return;
    setSending(true);
    try {
      if (isNoteMode) {
        await dispatch(addNote({ conversationId: activeConversationId, text }));
      } else {
        await dispatch(sendReply({ conversationId: activeConversationId, text, media }));
      }
    } finally {
      setSending(false);
    }
  };

  const handleResolve = () => {
    if (activeConversationId) {
      dispatch(updateConversationStatus({ conversationId: activeConversationId, status: 'resolved' }));
    }
  };

  const handleMarkPending = () => {
    if (activeConversationId) {
      dispatch(updateConversationStatus({ conversationId: activeConversationId, status: 'pending' }));
    }
  };

  const [markingLead, setMarkingLead] = useState(false);
  const handleMarkAsLead = async () => {
    if (!activeConversationId || markingLead) return;
    setMarkingLead(true);
    try {
      await dispatch(markConversationAsLead(activeConversationId)).unwrap();
    } finally {
      setMarkingLead(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row h-full min-h-0 bg-[#F7F9FB] overflow-hidden">
      {/* Left: Conversation List */}
      <div className="w-full lg:w-80 max-h-[40vh] lg:max-h-none flex-shrink-0 bg-white border-b lg:border-b-0 lg:border-r border-[#E5E5E5] flex flex-col">
        {/* Header + Filters */}
        <div className="p-4 border-b border-[#E5E5E5]">
          <h1 className="text-lg font-bold text-[#2E2E2E] mb-3">Inbox</h1>
          <input
            type="text"
            placeholder="Search conversations…"
            className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded focus:outline-none focus:ring-2 focus:ring-[#0176D3]"
            value={filters.search || ''}
            onChange={e => dispatch(setFilters({ search: e.target.value }))}
          />
          <div className="flex gap-1 mt-2 flex-wrap">
            {['all', 'whatsapp', 'instagram', 'messenger', 'telegram', 'email', 'voice', 'support_widget'].map(ch => (
              <button
                key={ch}
                onClick={() => dispatch(setFilters({ channel: ch }))}
                className={`px-2 py-1 text-xs rounded-full capitalize ${filters.channel === ch ? 'bg-[#0176D3] text-white' : 'bg-[#F3F2F2] text-[#5C5C5C]'}`}
              >
                {ch}
              </button>
            ))}
          </div>
          <div className="flex gap-1 mt-1 flex-wrap">
            {['open', 'pending', 'resolved'].map(st => (
              <button
                key={st}
                onClick={() => dispatch(setFilters({ status: st }))}
                className={`px-2 py-1 text-xs rounded-full capitalize ${filters.status === st ? 'bg-[#0176D3] text-white' : 'bg-[#F3F2F2] text-[#5C5C5C]'}`}
              >
                {st}
              </button>
            ))}
          </div>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto">
          {loading && <p className="text-center text-sm text-[#706E6B] mt-8">Loading…</p>}
          {!loading && conversations.length === 0 && (
            <p className="text-center text-sm text-[#706E6B] mt-8">No conversations</p>
          )}
          {conversations.map(conv => {
            const ch = CHANNEL_ICONS[conv.channel] || {};
            const isActive = conv._id === activeConversationId;
            const slaWarn = !conv.firstResponseAt && conv.status === 'open' && conv.firstMessageAt
              && (Date.now() - new Date(conv.firstMessageAt).getTime()) > 10 * 60 * 1000;
            return (
              <div
                key={conv._id}
                onClick={() => dispatch(setActiveConversation(conv._id))}
                className={`flex items-start gap-3 p-4 cursor-pointer hover:bg-[#F7F9FB] border-b border-[#F3F2F2] ${isActive ? 'bg-[#EAF4FE]' : ''}`}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                  style={{ backgroundColor: ch.color || '#6b7280' }}>
                  {(conv.contactName || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#2E2E2E] truncate">{conv.contactName || conv.contactPhone || 'Unknown'}</span>
                    <span className="text-xs text-[#706E6B] flex-shrink-0 ml-1">
                      {conv.lastMessageAt ? new Date(conv.lastMessageAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                  <p className="text-xs text-[#706E6B] truncate mt-0.5">{conv.lastMessagePreview || '...'}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-xs">{ch.emoji}</span>
                    {conv.unreadCount > 0 && (
                      <span className="bg-[#0176D3] text-white text-xs rounded-full px-1.5 py-0.5">{conv.unreadCount}</span>
                    )}
                    {slaWarn && <span className="text-xs text-[#BA0517] font-medium">⚠ SLA</span>}
                    {conv.assignedTo && (
                      <span className="text-xs text-[#706E6B]">{conv.assignedTo.firstName || conv.assignedTo.email}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right: Conversation Detail */}
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        {activeConv ? (
          <>
            {/* Conv Header */}
            <div className="flex-shrink-0 bg-white border-b border-[#E5E5E5] px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-[#2E2E2E]">
                  {activeConv.contactName || activeConv.contactPhone || 'Unknown'}
                </h2>
                <p className="text-xs text-[#706E6B] capitalize">
                  {CHANNEL_ICONS[activeConv.channel]?.emoji} {activeConv.channel} · {activeConv.contactPhone || ''}
                </p>
              </div>
              <div className="flex gap-2">
                <span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLORS[activeConv.status]}`}>{activeConv.status}</span>
                {AI_CHANNELS.includes(activeConv.channel) && AI_STATE_BADGE[activeConv.aiState || 'ai'] && (
                  <span className={`text-xs px-2 py-1 rounded-full ${AI_STATE_BADGE[activeConv.aiState || 'ai'].className}`}>
                    {AI_STATE_BADGE[activeConv.aiState || 'ai'].label}
                  </span>
                )}
                {AI_CHANNELS.includes(activeConv.channel) && (activeConv.aiState || 'ai') === 'ai' && (
                  <button onClick={() => dispatch(setConversationAiState({ conversationId: activeConv._id, aiState: 'human' }))} className="text-xs bg-[#F3F2F2] hover:bg-[#E5E5E5] text-[#444444] px-3 py-1 rounded whitespace-nowrap">
                    Take over
                  </button>
                )}
                {AI_CHANNELS.includes(activeConv.channel) && (activeConv.aiState || 'ai') !== 'ai' && (
                  <button onClick={() => dispatch(setConversationAiState({ conversationId: activeConv._id, aiState: 'ai' }))} className="text-xs bg-[#0176D3] hover:bg-[#014486] text-white px-3 py-1 rounded whitespace-nowrap">
                    Hand back to AI
                  </button>
                )}
                {activeConv.leadId ? (
                  <span className="text-xs bg-[#E3F3E8] text-[#036B3A] px-3 py-1 rounded-lg">Lead</span>
                ) : (
                  (activeConv.channel === 'whatsapp' || activeConv.channel === 'instagram') && (
                    <button
                      onClick={handleMarkAsLead}
                      disabled={markingLead}
                      className="text-xs bg-[#0176D3] hover:bg-[#014486] disabled:opacity-50 text-white px-3 py-1 rounded whitespace-nowrap"
                    >
                      {markingLead ? 'Marking…' : 'Mark as Lead'}
                    </button>
                  )
                )}
                {activeConv.status !== 'pending' && activeConv.status !== 'resolved' && (
                  <button onClick={handleMarkPending} className="text-xs bg-[#F3F2F2] hover:bg-[#E5E5E5] text-[#444444] px-3 py-1 rounded whitespace-nowrap">
                    Mark Pending
                  </button>
                )}
                {activeConv.status !== 'resolved' && (
                  <button onClick={handleResolve} className="text-xs bg-[#F3F2F2] hover:bg-[#E5E5E5] text-[#444444] px-3 py-1 rounded whitespace-nowrap">
                    Resolve
                  </button>
                )}
              </div>
            </div>

            {/* Messages */}
            <ConversationThread
              messages={messages}
              loading={messagesLoading}
              loadingMore={messagesLoadingMore}
              hasMoreHistory={!!messagesPagination && messagesPagination.page * messagesPagination.limit < messagesPagination.total}
              onLoadMore={() => dispatch(fetchMessages({ conversationId: activeConversationId, page: (messagesPagination?.page || 1) + 1 }))}
            />

            {/* Reply Box */}
            <ReplyBox
              isNoteMode={isNoteMode}
              onToggleNote={() => setIsNoteMode(v => !v)}
              onSend={handleSend}
              allowMedia={activeConv?.channel === 'telegram'}
              sending={sending}
              draft={draftGone === activeConversationId ? null : activeConv?.aiDraft}
              onDraftUsed={clearDraft}
              onDraftDismissed={clearDraft}
            />
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-[#706E6B]">
            <div className="text-center">
              <div className="text-5xl mb-4">💬</div>
              <p className="text-lg font-medium">Select a conversation</p>
              <p className="text-sm mt-1">Choose from the list on the left to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
